import SectionHeading from "@/components/SectionHeading";
import Counter from "@/components/Counter";
import { ShieldCheck, Compass, Users, TrendingUp } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { imgSrc } from "@/lib/api";
import SEO from "@/components/SEO";

const VALUE_ICONS = [ShieldCheck, Compass, Users, TrendingUp];

export default function About() {
  const { content } = useContent();
  const A = content.about, L = content.leadership;

  return (
    <div data-testid="about-page" className="pt-32">
      <SEO path="/about" title="About Us · Heritage Since 1986"
        description="Victory Aluminium & Hardwares — incorporated 1998, with trading roots since 1986. Led by Syed Ahamed Kabeer (MBA, Aston Business School). Backed by Star Aluminium & Alloys manufacturing." />
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pb-20">
        <SectionHeading eyebrow={A.eyebrow} title={A.title} sub={A.sub} />
      </section>

      <section className="relative">
        <img src={imgSrc(content.about_hero_image)} alt="Architecture" className="w-full h-[60vh] object-cover" />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="tracking-eyebrow text-[#D4AF37]">Our Story</span>
          <h3 className="font-display text-3xl md:text-4xl mt-4 leading-tight">{A.story_title}</h3>
        </div>
        <div className="lg:col-span-7 text-[#4B5563] leading-relaxed space-y-5 text-base">
          {A.story_paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          <p className="font-display text-2xl text-[#1A1A1A] pt-4 border-t border-[#E5E7EB]">&ldquo;{A.quote}&rdquo;</p>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <SectionHeading eyebrow="What we stand for" title="Principles that have outlasted trends." align="center" />
          <div className="grid md:grid-cols-4 gap-5 mt-14">
            {A.values.map((v, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <div key={v.title} className="border border-[#E5E7EB] p-7 hover:border-[#D4AF37] transition-colors">
                  <Icon className="w-7 h-7 text-[#D4AF37] mb-4" />
                  <h4 className="font-display text-xl">{v.title}</h4>
                  <p className="text-sm text-[#4B5563] mt-2">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#0F0F10] text-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-[#1A1A1A]">
              <img src={imgSrc(L.image)} alt={L.name} className="w-full h-full object-cover" />
            </div>
            <div className="mt-6">
              <div className="font-display text-2xl">{L.name}</div>
              <div className="text-sm text-[#9CA3AF] mt-1">{L.designation}</div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading invert eyebrow={L.eyebrow} title="A modern hand on a heritage business." sub={L.body} />
            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {L.points.map((p) => (
                <div key={p.title} className="border-l-2 border-[#D4AF37] pl-4">
                  <div className="font-display text-lg">{p.title}</div>
                  <div className="text-sm text-[#9CA3AF] mt-1">{p.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER LEGACY — light section above stats */}
      {content.founder?.enabled && (
        <section className="bg-white py-24 border-t border-[#E5E7EB]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden bg-[#F3F4F6]">
                {content.founder.image
                  ? <img src={imgSrc(content.founder.image)} alt={content.founder.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-[#9CA3AF] text-sm">Add a photo from /admin</div>}
              </div>
              <div className="mt-6">
                <div className="font-display text-2xl">{content.founder.name}</div>
                <div className="text-sm text-[#4B5563] mt-1">{content.founder.designation}</div>
                {content.founder.years_active && <div className="text-xs text-[#6B7280] mt-1 tracking-widest uppercase">{content.founder.years_active}</div>}
              </div>
            </div>
            <div className="lg:col-span-7">
              <SectionHeading eyebrow={content.founder.eyebrow} title={content.founder.title} sub={content.founder.body} />
              {Array.isArray(content.founder.points) && content.founder.points.length > 0 && (
                <div className="mt-10 grid sm:grid-cols-2 gap-5">
                  {content.founder.points.map((p) => (
                    <div key={p.title} className="border-l-2 border-[#D4AF37] pl-4">
                      <div className="font-display text-lg">{p.title}</div>
                      <div className="text-sm text-[#4B5563] mt-1">{p.desc}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
          {content.stats.map((s, i) => (
            <div key={i} className="bg-white px-6 py-10 text-center">
              <div className="text-[#D4AF37] text-4xl md:text-5xl"><Counter end={s.value} suffix={s.suffix} /></div>
              <div className="mt-3 tracking-eyebrow text-[#4B5563]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
